import React from "react";
import { cn } from "~/lib/utils";
import { Button } from "./Button";
import { useState, useEffect, useRef } from "react";

export interface HeroSlide {
  id: string;
  title: string;
  description: string;
  imageUrl: string;
  ctaText: string;
  ctaLink: string;
  backgroundColor?: string;
}

export interface HeroSectionProps {
  slides: HeroSlide[];
  autoPlay?: boolean;
  interval?: number;
  className?: string;
  height?: "sm" | "default" | "lg" | "xl";
}

export function HeroSection({
  slides,
  autoPlay = true,
  interval = 5000,
  className,
  height = "default",
}: HeroSectionProps) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  const heightClasses = {
    sm: "h-64 md:h-80",
    default: "h-80 md:h-96",
    lg: "h-96 md:h-[32rem]",
    xl: "h-[32rem] md:h-[40rem]",
  };

  // Auto-play functionality
  useEffect(() => {
    if (autoPlay && slides.length > 1) {
      intervalRef.current = setInterval(() => {
        setCurrentIndex((prev) => (prev + 1) % slides.length);
      }, interval);

      return () => {
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
        }
      };
    }
  }, [autoPlay, interval, slides.length]);

  const goToSlide = (index: number) => {
    setCurrentIndex(index);
  };

  const goToPrevious = () => {
    setCurrentIndex((prev) => (prev === 0 ? slides.length - 1 : prev - 1));
  };

  const goToNext = () => {
    setCurrentIndex((prev) => (prev + 1) % slides.length);
  };

  if (slides.length === 0) {
    return null;
  }

  return (
    <section
      className={cn(
        "relative overflow-hidden",
        heightClasses[height],
        className
      )}
    >
      {/* Slides */}
      <div className="relative h-full">
        {slides.map((slide, index) => (
          <div
            key={slide.id}
            className={cn(
              "absolute inset-0 transition-opacity duration-500",
              index === currentIndex ? "opacity-100" : "opacity-0"
            )}
          >
            <HeroSlideItem slide={slide} />
          </div>
        ))}
      </div>

      {/* Navigation arrows */}
      {slides.length > 1 && (
        <>
          <button
            onClick={goToPrevious}
            className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/40 hover:bg-white/75 text-gray-800 dark:bg-gray-800 dark:hover:bg-gray-700 dark:text-gray-200 rounded-full p-2 shadow-md transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500"
            aria-label="Previous slide"
          >
            <svg
              className="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 19l-7-7 7-7"
              />
            </svg>
          </button>
          <button
            onClick={goToNext}
            className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/40 hover:bg-white/75 text-gray-800 dark:bg-gray-800 dark:hover:bg-gray-700 dark:text-gray-200 rounded-full p-2 shadow-md transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500"
            aria-label="Next slide"
          >
            <svg
              className="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 5l7 7-7 7"
              />
            </svg>
          </button>
        </>
      )}

      {/* Dots indicator */}
      {slides.length > 1 && (
        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex space-x-1 scale-50">
          {slides.map((_, index) => (
            <button
              key={index}
              onClick={() => goToSlide(index)}
              className={cn(
                "w-1 h-1 rounded-full transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-200 dark:hover:bg-gray-300 scale-50",
                currentIndex === index
                  ? "bg-white/75 scale-50 dark:bg-gray-200/75"
                  : "bg-white/40 hover:bg-white/75 dark:bg-gray-200/40 dark:hover:bg-gray-300/75"
              )}
              aria-label={`Go to slide ${index + 1}`}
            />
          ))}
        </div>
      )}
    </section>
  );
}

function HeroSlideItem({ slide }: { slide: HeroSlide }) {
  return (
    <div
      className="relative h-full w-full flex items-center justify-center"
      style={{ backgroundColor: slide.backgroundColor || "#f8fafc" }}
    >
      {/* Background Image */}
      <div
        className="absolute inset-0 bg-cover bg-center bg-no-repeat"
        style={{ backgroundImage: `url(${slide.imageUrl})` }}
      >
        <div className="absolute inset-0 bg-black/30" />
      </div>

      {/* Content */}
      <div className="relative z-10 text-center text-white px-4 max-w-4xl mx-auto">
        <h1 className="text-3xl md:text-5xl lg:text-6xl font-bold mb-4 leading-tight">
          {slide.title}
        </h1>
        <p className="text-lg md:text-xl lg:text-2xl mb-8 opacity-90 max-w-2xl mx-auto">
          {slide.description}
        </p>
        <Button
          size="lg"
          variant="primary"
          className="bg-white text-gray-900 hover:bg-gray-100 shadow-lg"
          onClick={() => {
            if (slide.ctaLink.startsWith("http")) {
              window.open(slide.ctaLink, "_blank");
            } else {
              window.location.href = slide.ctaLink;
            }
          }}
        >
          {slide.ctaText}
        </Button>
      </div>
    </div>
  );
}

// Specialized hero section for recipes
export function RecipeHeroSection({
  featuredRecipe,
  className,
}: {
  featuredRecipe?: {
    id: string;
    title: string;
    description: string;
    imageUrl: string;
    category: string;
  };
  className?: string;
}) {
  if (!featuredRecipe) {
    return null;
  }

  return (
    <section
      className={cn(
        "relative h-80 md:h-96 overflow-hidden rounded-lg",
        className
      )}
    >
      <div
        className="absolute inset-0 bg-cover bg-center bg-no-repeat"
        style={{ backgroundImage: `url(${featuredRecipe.imageUrl})` }}
      >
        <div className="absolute inset-0 bg-gradient-to-r from-black/60 to-black/30" />
      </div>

      <div className="relative z-10 h-full flex items-center px-6 md:px-12">
        <div className="text-white max-w-2xl">
          <div className="inline-block bg-blue-600 text-white px-3 py-1 rounded-full text-sm font-medium mb-4">
            {featuredRecipe.category}
          </div>
          <h2 className="text-3xl md:text-4xl font-bold mb-4 leading-tight">
            {featuredRecipe.title}
          </h2>
          <p className="text-lg md:text-xl mb-6 opacity-90">
            {featuredRecipe.description}
          </p>
          <Button
            size="lg"
            variant="primary"
            onClick={() => {
              window.location.href = `/recipes/${featuredRecipe.id}`;
            }}
          >
            레시피 보기
          </Button>
        </div>
      </div>
    </section>
  );
}
